const AUTH_REDIRECT_STORAGE_KEY = 'auth:returnTo';
export const AUTH_REDIRECT_STATE_KEY = 'fromAuthRedirect';
export const AUTH_REDIRECT_TTL_MS = 2 * 60 * 1000;

const isAuthPath = (path) => path === '/auth' || path.startsWith('/auth/');

const getSessionStorage = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch (_) {
    return null;
  }
};

export function getPathFromLocation(location) {
  if (!location || typeof location.pathname !== 'string') {
    return null;
  }

  const search = typeof location.search === 'string' ? location.search : '';
  const hash = typeof location.hash === 'string' ? location.hash : '';
  return `${location.pathname}${search}${hash}`;
}

export function getCurrentWindowPath() {
  if (typeof window === 'undefined') {
    return null;
  }

  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

export function normalizeAuthRedirectPath(path) {
  if (typeof path !== 'string') {
    return null;
  }

  const trimmedPath = path.trim();
  if (!trimmedPath.startsWith('/') || trimmedPath.startsWith('//') || isAuthPath(trimmedPath)) {
    return null;
  }

  return trimmedPath;
}

export function buildAuthRedirectState(locationOrPath) {
  const rawPath = typeof locationOrPath === 'string' ? locationOrPath : getPathFromLocation(locationOrPath);
  const redirectPath = normalizeAuthRedirectPath(rawPath);

  return redirectPath ? { [AUTH_REDIRECT_STATE_KEY]: redirectPath } : undefined;
}

export function persistAuthRedirectPath(path) {
  const redirectPath = normalizeAuthRedirectPath(path);
  const storage = getSessionStorage();

  if (!redirectPath || !storage) {
    return null;
  }

  storage.setItem(
    AUTH_REDIRECT_STORAGE_KEY,
    JSON.stringify({
      path: redirectPath,
      savedAt: Date.now(),
    }),
  );
  return redirectPath;
}

export function persistAuthRedirectState(state) {
  if (!state || typeof state !== 'object') {
    return null;
  }

  return persistAuthRedirectPath(state[AUTH_REDIRECT_STATE_KEY]);
}

function readStoredAuthRedirect() {
  const storage = getSessionStorage();
  if (!storage) {
    return { path: null, expired: false, invalid: false };
  }

  const rawValue = storage.getItem(AUTH_REDIRECT_STORAGE_KEY);
  if (!rawValue) {
    return { path: null, expired: false, invalid: false };
  }

  try {
    const parsedValue = JSON.parse(rawValue);

    // Handle if the JSON was just a plain string e.g., JSON.parse('"/dashboard"')
    if (typeof parsedValue === 'string') {
      const path = normalizeAuthRedirectPath(parsedValue);
      return { path, expired: false, invalid: !path };
    }

    const path = normalizeAuthRedirectPath(parsedValue?.path);
    const savedAt = parsedValue?.savedAt;

    // If the path isn't valid, flag it as invalid
    if (!path) {
      return { path: null, expired: false, invalid: true };
    }

    // Explicitly check for valid number instead of relying on Number(null) === 0
    if (typeof savedAt !== 'number' || Date.now() - savedAt > AUTH_REDIRECT_TTL_MS) {
      return { path: null, expired: true, invalid: false };
    }

    return { path, expired: false, invalid: false };
  } catch (_) {
    // Handle pre-JSON legacy strings e.g., '/dashboard' string written to localstorage
    const legacyPath = normalizeAuthRedirectPath(rawValue);
    return { path: legacyPath, expired: false, invalid: !legacyPath };
  }
}

export function readAuthRedirectPath() {
  return readStoredAuthRedirect().path;
}

export function clearAuthRedirectPath() {
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }

  storage.removeItem(AUTH_REDIRECT_STORAGE_KEY);
}

const getFallbackPath = (fallbackPath) => normalizeAuthRedirectPath(fallbackPath) || '/';

export function resolveAuthRedirectPath(fallbackPath = '/', expiredFallbackPath = fallbackPath) {
  const { path, expired, invalid } = readStoredAuthRedirect();

  if (path) {
    return path;
  }

  // Clear storage if the value is expired or we flagged it as invalid garbage
  if (expired || invalid) {
    clearAuthRedirectPath();
  }

  return getFallbackPath(expired ? expiredFallbackPath : fallbackPath);
}

export function consumeAuthRedirectPath(fallbackPath = '/', expiredFallbackPath = fallbackPath) {
  const redirectPath = resolveAuthRedirectPath(fallbackPath, expiredFallbackPath);
  clearAuthRedirectPath();
  return redirectPath;
}
